<script setup lang="ts">
//======================================================================//
//                           IMPORTS                                    //
//======================================================================//
import { ref, computed, onBeforeMount, onBeforeUnmount } from "vue"
import { useGraphStore } from "@modules/data/stores/graph.store"
import { useEventBus, useThrottleFn } from "@vueuse/core"
import { formatDate, formatIdSource } from "@core/helpers"
import { useRedirect } from "@core/composables"
import {
    handleBuildingCondition,
    handleSourceCondition,
    handleUserCondition,
} from "@modules/data/helpers"
import type {
    ErrorMessage,
    ChartSource,
} from "@modules/data/components/data/chart/PlotlyChartCard.vue"
import type {
    Building,
    BuildingSource,
    Source,
    Freq,
    DataVariable,
    ConditionErrorMessage,
} from "@types"
import { useI18n } from "vue-i18n"
import moment from "moment"
import {
    VISIBLE_CONDITIONS_COMBINE_GRAPH_DEFAULT,
    blockOmitFromGraphSelector,
} from "@modules/data/utils/evaluateVisibleConditionsBlocks"

//======================================================================//
//                           Props & Composables                        //
//======================================================================//
type Props = {
    /**
     * Identifiant du graphique
     */
    graphId: string
    /**
     * Indique si le graphique est pour une source ou un bâtiment.
     * Conditionne les services utilisés pour récupérer les données.
     */
    type: "source" | "building"
    /**
     * Bâtiment courant (fourni par le parent quand type="building").
     */
    building?: Building | null
    /**
     * Sources du bâtiment (fourni par le parent quand type="building").
     */
    sources?: Partial<BuildingSource>[]
    /**
     * Source courante (fourni par le parent quand type="source").
     */
    source?: Source | null
    /**
     * Liste des graphiques cachés.
     */
    graphHidden?: string[]

    /**
     * Désactive le bouton de suppression du graphique
     */
    disableRemove?: boolean
}

const emit = defineEmits(["click:remove"])
const props = withDefaults(defineProps<Props>(), {
    building: null,
    sources: () => [],
    source: null,
    graphHidden: () => [],
    disableRemove: false,
})

const graphStore = useGraphStore()
const bus = useEventBus<string>("news")
const { redirectWrapper } = useRedirect()

/** Bâtiment courant (depuis props, pour conditions et affichage). */
const contextBuilding = computed(() => props.building ?? null)
/** Sources à utiliser pour les conditions (bâtiment: props.sources, source: [props.source]). */
const contextSources = computed(() =>
    props.type === "source" && props.source
        ? [props.source]
        : (props.sources ?? [])
)

const { t } = useI18n({
    messages: {
        fr: {
            remove_graph:
                '<p>Masquer</p><p class="font-light text-sm">Reselectionner le graphique dans le menu en haut pour le réafficher.</p>',
            graph_placeholder: "Sélectionner un graphique",
            source_button: "Accéder aux données de la source",
            footer_messages: {
                invalid_pdl_not_exist: `Enedis nous a remonté que le compteur électrique <strong>{idsource}</strong> n'existe pas sur le réseau. Nous vous invitons à contacter Enedis au <a href="tel:09 70 83 19 70"><strong class="strong--small">09 70 83 19 70</strong></a> et dire “Je souhaite parler à un conseiller." pour comprendre la situation. | Enedis nous a remonté que les compteurs électriques <strong>{idsource}</strong> n'existent pas sur le réseau. Nous vous invitons à contacter Enedis au <a href="tel:09 70 83 19 70"><strong class="strong--small">09 70 83 19 70</strong></a> et dire “Je souhaite parler à un conseiller." pour comprendre la situation.`,
                invalid_pce_dda:
                    "GRDF a besoin du consentement du titulaire du contrat pour continuer de donner accès aux données de gaz pour le compteur {idsource}. Rendez-vous sur la page de vos compteurs pour signer le consentement. | GRDF a besoin du consentement du titulaire du contrat pour continuer dedonner accès aux données de gaz pour les compteurs suivants : {idsource}. Rendez-vous sur la page de vos compteurs pour signer les consentements.",
                invalid_pdl_not_linky: `Le compteur électrique : {idsource} ajouté sur le bâtiment n'est pas communiquant ou ne fonctionnent pas correctement.
      Nous vous invitons à contacter Enedis au <a href="tel:09 70 83 19 70"><strong class="strong--small">09 70 83 19 70</strong></a> et dire “Je souhaite parler à un conseiller”. Après moins de 5 min d'attente, ils seront en mesure de vous accompagner dans le changement du compteur. | Les compteurs électriques : {idsource} ajoutés sur le bâtiment ne sont pas communiquant ou ne fonctionnent pas correctement.
      Nous vous invitons à contacter Enedis au <a href="tel:09 70 83 19 70"><strong class="strong--small">09 70 83 19 70</strong></a> et dire “Je souhaite parler à un conseiller”. Après moins de 5 min d'attente, ils seront en mesure de vous accompagner dans le changement du compteur.`,
            },
        },
    },
})

//======================================================================//
//                           Data                                       //
//======================================================================//
/**
 * Le graphique sélectionné dans la carte
 */
const modelGraph = ref<string>("")
/**
 * Indique si le modal est ouvert
 */
const modalToggle = ref(false)
/**
 * Information sur le graphique plotly récupéré
 */
type Period = {
    date_from: string
    date_to: string
}

type GraphJson = {
    plotly_graph_id: string
    error?: string
    graph: {
        data: object[]
        layout: object
        config: object
    } | null
    sources?: string[]
    availableVariables?: DataVariable[]
    freq?: string
    /**
     * Contexte additionnel (ex: conditions non remplies pour l'affichage du graphique)
     */
    context?: Record<string, unknown>
}

const graphJson = ref<GraphJson[]>([])

//======================================================================//
//                           Computed                                   //
//======================================================================//
/**
 * Récupère les configurations du graphique dans le fichier de configuration
 * en le recherchant à l'aide du graphId
 * @returns {Object|null} - configurations du graphique
 */
const getGraph = computed(() => {
    const graph = graphStore.getGraph(props.graphId)
    return graph
        ? {
              ...graph,
              graph_list: graph.graph_list.filter(
                  (gl) => !props.graphHidden.includes(gl.id)
              ),
          }
        : null
})

/**
 * Récupère le graphique actuellement sélectionné dans la liste des graphiques calculés
 */
const getCurrentGraph = computed(() => {
    return (
        graphJson.value.find((g) => g.plotly_graph_id === modelGraph.value) ??
        null
    )
})

/**
 * Récupère la configuration du graphique sélectionné
 */
const getGraphConfigSelected = computed(() => {
    return getGraph.value?.graph_list.find((g) => g.id === modelGraph.value)
})

/**
 * Récupère les noms de chaque graphique associé au même graphId.
 * @returns {Array} - Liste des noms des graphiques disponibles.
 */
const getGraphOptionList = computed(() => {
    return (
        getGraph.value?.graph_list
            .filter((graph) => {
                // Filtre sélecteur : blocs avec omit_from_graph_selector (ex-exclusive)
                if (
                    graph.visible_conditions?.some((c) =>
                        blockOmitFromGraphSelector(c)
                    )
                ) {
                    return graph.visible_conditions.some(
                        ({ conditions, ...blockRest }) => {
                            if (!blockOmitFromGraphSelector(blockRest)) {
                                return true
                            }
                            return conditions.some((condition) => {
                                if (condition.origin === "user") {
                                    return handleUserCondition(condition)
                                }
                                if (
                                    condition.origin === "building" &&
                                    !!contextBuilding.value
                                ) {
                                    return handleBuildingCondition(
                                        contextBuilding.value,
                                        condition
                                    )
                                }

                                return handleSourceCondition(
                                    contextSources.value,
                                    condition
                                )
                            })
                        }
                    )
                }
                return true
            })
            .map(
                (g: {
                    name: string
                    id: string
                    icon?: string
                    icon_color?: string
                }) => ({
                    name: g.name,
                    value: g.id,
                    icon: g.icon,
                    icon_color: g.icon_color,
                })
            ) ?? []
    )
})

/**
 * Récupère le message d'erreur à afficher en fonction de l'erreur survenue.
 */
const getMessage = computed<ErrorMessage | null>(() => {
    if (!getCurrentGraph.value || !getCurrentGraph.value?.error) {
        return null
    }
    switch (getCurrentGraph.value.error) {
        case "invalid_freq":
            return {
                type: "invalid_freq",
                message:
                    "La fréquence sélectionnée n'est pas compatible avec ce graphique.",
                action: {
                    message: "Changer la fréquence",
                    type: "change_freq",
                    freq: getGraphConfigSelected.value?.freq[0] ?? "daily",
                } as {
                    message: string
                    type: "change_freq" | "redirect" | "open_modal"
                    freq: string
                },
            }
        case "invalid_condition": {
            const conditionContext = getCurrentGraph.value.context as {
                errorMessages: ConditionErrorMessage[]
            }
            return {
                type: "invalid_condition",
                message:
                    conditionContext.errorMessages?.[0]?.message ||
                    "Les conditions pour afficher ce graphique ne sont pas remplies.",
                action: conditionContext.errorMessages?.[0]?.action || null,
            }
        }
        case "error_loading":
            return {
                type: "error_loading",
                message: "Erreur lors du chargement du graphique.",
                action: {
                    message: "Réessayer",
                    type: "retry",
                },
            }
        case "empty-data": {
            const isCdcGraph = getCurrentGraph.value?.freq === "hourly"
            const delayedPdl = [...(props.sources ?? []), props.source].find(
                (s) =>
                    s?.type === "pdl" &&
                    (s as Source)?.informations?.pdl?.data_retrieval?.historical
                        ?.state === "delayed"
            ) as Source | undefined

            if (isCdcGraph && delayedPdl) {
                return {
                    type: "empty-data",
                    message:
                        "Courbe de charge en cours d'activation chez Enedis. Les premières mesures peuvent mettre jusqu'à 3 jours à remonter.",
                }
            }

            let message = "Aucune donnée disponible sur cette période."
            let dates: {
                variable: string
                freqs: { freq: string; date_from: string; date_to: string }[]
            }[] = []

            // Si des variables sont disponibles, on les utilise pour afficher la période de disponibilité des données
            if (getCurrentGraph.value?.availableVariables?.length) {
                // Calcules des périodes de disponibilité des données pour chaque variable
                dates = getCurrentGraph.value.availableVariables.map(
                    (variable) => {
                        return {
                            variable: variable.type,
                            freqs: Object.entries(variable)
                                .filter(
                                    ([key]) =>
                                        key !== "type" &&
                                        key === getCurrentGraph.value?.freq
                                )
                                //
                                .filter((entry): entry is [string, Period] => {
                                    const [, value] = entry
                                    return (
                                        typeof value === "object" &&
                                        value !== null &&
                                        "date_from" in value &&
                                        "date_to" in value &&
                                        (value as Period).date_from !== null &&
                                        (value as Period).date_to !== null
                                    )
                                })
                                .map(([key, value]) => ({
                                    freq: key,
                                    date_from: value.date_from,
                                    date_to: value.date_to,
                                })),
                        }
                    }
                )

                // Si on a des dates, on vérifie si la période sélectionnée est dans la plage disponible
                if (dates.length > 0) {
                    // Trouve la date min et la date max de toutes les variables
                    const minDate = new Date(
                        Math.min(
                            ...dates
                                .flatMap((d) => d.freqs)
                                .map((f) => new Date(f.date_from).getTime())
                        )
                    )
                    const maxDate = new Date(
                        Math.max(
                            ...dates
                                .flatMap((d) => d.freqs)
                                .map((f) => new Date(f.date_to).getTime())
                        )
                    )

                    // Récupérer la période sélectionnée par l'utilisateur
                    const userPeriod = graphStore.period
                    const userStartDate = moment(userPeriod[0])
                    const userEndDate = moment(userPeriod[1])

                    // Convertir les dates min/max en moment
                    const minMoment = moment(minDate)
                    const maxMoment = moment(maxDate)

                    // Vérifier si la période utilisateur est comprise dans la plage de données disponibles
                    const isUserPeriodInRange =
                        userStartDate.isSameOrAfter(minMoment) &&
                        userEndDate.isSameOrBefore(maxMoment)

                    if (isUserPeriodInRange) {
                        message =
                            "Il n'y a pas de donnée spécifiquement sur la période sélectionnée."
                    } else {
                        message =
                            "Vous êtes en dehors de la plage de données disponibles."
                    }

                    return {
                        type: "empty-data",
                        message,
                        period: {
                            freq: getCurrentGraph.value.freq as Freq,
                            date_from: isNaN(minDate.getTime())
                                ? null
                                : formatDate(minDate, "YYYY-MM-DD"),
                            date_to: isNaN(maxDate.getTime())
                                ? null
                                : formatDate(maxDate, "YYYY-MM-DD"),
                        },
                    }
                }
            }

            return {
                type: "empty-data",
                message,
            }
        }
        default:
            return null
    }
})

/**
 * Récupère les messages affichées dans le footer du composant en fonction de la configuration du graphique
 */
const getFooterMessage = computed(() => {
    if (!getGraph.value) {
        return []
    }

    // si pas de message configuré alors retourne un tableau vide
    if (!getGraph.value.footer_messages?.length) return []
    // Vérifie pour chaque message si les conditions sont remplies
    return getGraph.value.footer_messages
        ?.filter((message) => {
            return message.conditions.every((condition) => {
                if (condition.origin === "user") {
                    return handleUserCondition(condition)
                }
                if (
                    condition.origin === "building" &&
                    !!contextBuilding.value
                ) {
                    return handleBuildingCondition(
                        contextBuilding.value,
                        condition
                    )
                }
                return handleSourceCondition(contextSources.value, condition)
            })
        })
        .map(({ message, conditions, action, closable, severity }) => {
            if (typeof message === "string") {
                return {
                    message,
                    action,
                    closable: !!closable,
                }
            }

            const sources = contextSources.value.filter((src) =>
                conditions.every((condition) => {
                    if (condition.origin === "user") {
                        return handleUserCondition(condition)
                    }
                    if (
                        condition.origin === "building" &&
                        !!contextBuilding.value
                    ) {
                        return handleBuildingCondition(
                            contextBuilding.value,
                            condition
                        )
                    }
                    return handleSourceCondition([src], condition)
                })
            )

            // Sinon on map avec la traduction en ajoutant les idsource des sources dans le message
            return {
                message: t(
                    `footer_messages.${message.key}`,
                    {
                        idsource: sources
                            .map((s) =>
                                formatIdSource(s.idsource, s.type || s.idtype)
                            )
                            .join(", "),
                    },
                    sources?.length ?? 0
                ),
                action,
                closable: !!closable,
                severity: severity ?? "info",
            }
        })
})

/**
 * Indique si le graphique est en cours de chargement
 */
const isGraphLoading = computed(() => {
    return graphStore.isGraphLoading(props.graphId)
})

/**
 * Récupère la liste des sources associées au graphique en cours
 */
const getChartSourcesList = computed<ChartSource[]>(() => {
    return (
        getCurrentGraph.value?.sources
            ?.map((_id_source) => {
                const source = contextSources.value.find(
                    (s) => s._id_source === _id_source
                )
                if (!source) return null
                return source
            })
            .filter((s) => s !== null) ?? []
    )
})

const getGraphOptionValue = computed(() => {
    return getGraphOptionList.value?.find((o) => o.value === modelGraph.value)
})

//======================================================================//
//                           Fonction                                   //
//======================================================================//
const throttledFetchGraphData = useThrottleFn(() => {
    fetchGraphData()
}, 2000)

/**
 * Réessai manuel : pas de rafale vers DAPI (throttle) ni pendant un chargement déjà en file pour ce graphique.
 */
function onGraphRetryClick(): void {
    if (graphStore.isGraphLoading(props.graphId)) {
        return
    }
    throttledFetchGraphData()
}

const unsubscribe = bus.on((event: string, payload?: unknown) => {
    if (event === "graph:updated") {
        const index = graphJson.value.findIndex(
            (g) => g.plotly_graph_id === modelGraph.value
        )
        if (index !== -1) {
            graphJson.value[index].error = ""
        }
        fetchGraphData()
    }
    if (event === "graph:scroll") {
        // vérifie si le type d'indicateur est dans la liste des variables du graphique

        const { type: indicatorType, graph_id: indicatorGraphId } = payload as {
            type: string
            graph_id: string
        }

        // si le graphique de cette indicateur n'est pas le graphique actuel alors on
        // ne fait rien
        if (
            !getGraphOptionList.value.find((g) => g.value === indicatorGraphId)
        ) {
            return
        }

        // si le graphique de cette indicateur n'est pas le graphique actuel alors on
        // change le graphique actuel et on récupère les données du graphique
        if (getCurrentGraph.value?.plotly_graph_id !== indicatorGraphId) {
            modelGraph.value = indicatorGraphId
            fetchGraphData()
        }

        const variablesList =
            getGraph.value?.graph_list.flatMap((g) =>
                g.datasets.flatMap((d) => d.type)
            ) ?? []

        if (!variablesList.includes(indicatorType)) {
            return
        }

        // scroll jusqu'au graphique
        setTimeout(() => {
            document
                .getElementById(`graph-${modelGraph.value}`)
                ?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                    inline: "center",
                })
        }, 200)
    }
})

/**
 * Fonction asynchrone pour récupérer les données du graphique en format json
 */
async function fetchGraphData() {
    if (!getGraph.value) {
        return
    }

    const plotyChartId = getGraph.value.graph_list?.find(
        (g) => g.id === modelGraph.value
    )?.id

    if (!plotyChartId) {
        return
    }

    // Vérifier que la fréquence est compatible avec le graphique
    if (!getGraphConfigSelected.value?.freq.includes(graphStore.freq)) {
        updateGraphJsonList({
            plotly_graph_id: modelGraph.value,
            error: "invalid_freq",
            graph: null,
        })

        return
    }

    // Vérifie si la condition de visibilité est remplie
    if (getGraphConfigSelected.value?.visible_conditions?.length ?? 0 > 0) {
        const conditionGroupValidList: boolean[] = []
        const errorMessages: ConditionErrorMessage[] = []
        // Pour chaque groupe de condition, on vérifie que les conditions sont remplies
        for (const { conditions, error } of getGraphConfigSelected.value
            .visible_conditions ?? []) {
            // Vérifie si les conditions sont remplies pour chaque condition
            const conditionsCheckList: {
                valid: boolean
                error: ConditionErrorMessage | null
            }[] = conditions.reduce(
                (
                    acc: {
                        valid: boolean
                        error: ConditionErrorMessage | null
                    }[],
                    condition
                ) => {
                    let isValid = false

                    if (condition.origin === "user") {
                        isValid = handleUserCondition(condition)
                    } else if (
                        condition.origin === "building" &&
                        !!contextBuilding.value
                    ) {
                        isValid = handleBuildingCondition(
                            contextBuilding.value,
                            condition
                        )
                    } else {
                        isValid = handleSourceCondition(
                            contextSources.value,
                            condition
                        )
                    }

                    acc.push({
                        valid: isValid,
                        error: isValid || !error ? null : error,
                    })

                    return acc
                },
                []
            )

            // Vérifie si tous les conditions sont remplies dans un groupe de conditions
            const isGroupConditionValid = conditionsCheckList.every(
                (c) => c.valid
            )

            conditionGroupValidList.push(isGroupConditionValid)
            if (!isGroupConditionValid) {
                const invalidCondition = conditionsCheckList.find(
                    (c) => !c.valid
                )
                if (invalidCondition?.error) {
                    errorMessages.push(invalidCondition.error)
                }
            }
        }
        // Combine les blocs : "and" (tous requis) ou "or" (au moins un bloc entièrement valide)
        const combine =
            getGraphConfigSelected.value.visible_conditions_combine ??
            VISIBLE_CONDITIONS_COMBINE_GRAPH_DEFAULT
        const anyGroupFails = conditionGroupValidList.some((c) => !c)
        const allGroupsFail = conditionGroupValidList.every((c) => !c)
        const shouldReject =
            combine === "and"
                ? anyGroupFails
                : allGroupsFail && conditionGroupValidList.length > 0

        if (shouldReject) {
            updateGraphJsonList({
                plotly_graph_id: modelGraph.value,
                error: "invalid_condition",
                graph: null,
                context: {
                    errorMessages,
                },
            })
            return
        }
    }

    graphStore.enqueueRequest(
        props.type,
        props.graphId,
        plotyChartId,
        graphStore.period,
        graphStore.freq,
        onGraphUpdated
    )
}

/**
 * Callback appelé après un appel API pour récupérer les données du graphique
 * @param data - données du graphique ou null si une erreur est survenue
 */
function onGraphUpdated(
    data: {
        data: string
        sources?: string[]
        availableVariables?: DataVariable[]
        freq: string
    } | null
) {
    if (!data?.data) {
        updateGraphJsonList({
            plotly_graph_id: modelGraph.value,
            error: "error_loading",
            graph: null,
            sources: data?.sources,
        })
        return
    }

    type PlotlyTrace = { y?: unknown[] }
    type PlotlyFigure = { data?: PlotlyTrace[] }

    const dataParse = JSON.parse(data?.data) as PlotlyFigure

    function isDataEmpty(parsed: PlotlyFigure): boolean {
        if (!parsed.data?.length) {
            return true
        }

        if (parsed.data.every((d) => !d.y?.length)) {
            return true
        }
        return false
    }

    const body: GraphJson = {
        plotly_graph_id: modelGraph.value,
        graph: dataParse,
        error: isDataEmpty(dataParse) ? "empty-data" : "",
        availableVariables: data?.availableVariables ?? [],
        freq: data?.freq,
        sources: data.sources,
    }

    updateGraphJsonList(body)
}

/**
 * Mise à jour du graphique dans la liste des graphiques récupérés par API
 * @param body
 */
function updateGraphJsonList(body: GraphJson) {
    // si existe déjà un graphique avec le même id, le remplace
    const index = graphJson.value.findIndex(
        (g) => g.plotly_graph_id === modelGraph.value
    )
    if (index !== -1) {
        graphJson.value[index] = body
        return
    } else {
        graphJson.value.push(body)
    }
}

//======================================================================//
//                           LifeCycle                                  //
//======================================================================//
onBeforeMount(() => {
    // Met en place le premier graphique de la liste
    modelGraph.value = getGraphOptionList.value[0]?.value
    // récupère les données du graphique
    fetchGraphData()
})

onBeforeUnmount(() => {
    unsubscribe()
})
//======================================================================//
//                           onClick                                    //
//======================================================================//
/**
 * Quand l'utilisateur clique sur "Accéder aux données de la/les sources"
 * il est redirigé vers la page de la source si il n'y en a qu'une cela
 * enclenche l'ouverture d'une modal avec la liste des sources si il y en
 * a plusieurs
 */
function onOpenModalDataRaw(): void {
    if (getChartSourcesList?.value.length > 1)
        modalToggle.value = !modalToggle.value
    else {
        redirectWrapper("/source/:_id_source/data", {
            _id_source: getChartSourcesList?.value?.[0]._id_source,
        })
    }
}

/**
 * Lorsque l'utilisateur clique sur la croix, le graphique est retiré de
 * la liste des graphiques selectionnés
 */
function onRemoveGraph(): void {
    emit("click:remove", props.graphId)
}

function onClickChangeDates({
    date_from,
    date_to,
}: {
    date_from?: string
    date_to?: string
}) {
    graphStore.period = [new Date(date_from!), new Date(date_to!)]
}
</script>

<template>
    <PlotlyChartCard
        :card-id="`graph-${getCurrentGraph?.plotly_graph_id}`"
        :title="getGraph?.name ?? ''"
        :graph="getCurrentGraph?.graph ?? null"
        :loading="isGraphLoading || !!getCurrentGraph?.error"
        :graph-option-list="getGraphOptionList"
        v-model:model-graph="modelGraph"
        :graph-option-value="getGraphOptionValue"
        :graph-placeholder="t('graph_placeholder')"
        :show-remove-button="!disableRemove"
        :remove-tooltip="t('remove_graph')"
        :error-message="getMessage"
        :current-building-id="contextBuilding?._id"
        :current-source-id="
            type === 'source' && source ? source._id_source : undefined
        "
        :show-source-button="
            type === 'building' &&
            !!getChartSourcesList?.length &&
            graphStore.isActionAuthorised
        "
        :source-button-label="t('source_button')"
        :chart-sources-list="getChartSourcesList ?? []"
        v-model:modal-visible="modalToggle"
        :footer-messages="getFooterMessage"
        @update:model-graph="fetchGraphData"
        @click:remove="onRemoveGraph"
        @click:dates="onClickChangeDates"
        @click:freq="graphStore.freq = $event"
        :error-action-disabled="graphStore.isGraphLoading(props.graphId)"
        @click:retry="onGraphRetryClick"
        @click:source="onOpenModalDataRaw"
    />
</template>
